import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { MontageplanPersonalPlanningOverviewComponent } from '@work-frontend-monorepo/work-ui/helpers';
import { WorkMontageplanV2Service } from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'sfg-app-mitarbeitende-home-page',
  templateUrl: './mitarbeitende-home-page.component.html',
  styleUrls: ['./mitarbeitende-home-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    ReactiveFormsModule,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    MontageplanPersonalPlanningOverviewComponent,
    TranslatePipe,
  ],
})
export class MitarbeitendeHomePageComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly workMontageplanV2Service = inject(WorkMontageplanV2Service);

  protected profile = toSignal(this.store.select(ProfileState.profile));
  protected toDate = signal<string>(dayjs().format('YYYY-MM-DD'));

  protected greeting = computed(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'BEGRUESSUNG_MORGEN';
    if (hour >= 12 && hour < 18) return 'BEGRUESSUNG_INFORMELL';
    return 'BEGRUESSUNG_ABEND';
  });

  /** Übersetzungs-Key plus Platzhalter für den Titel. */
  protected welcomeMessage = computed<{ key: string; params: { firstname?: string } }>(() => {
    const profile = this.profile();
    if (profile) return { key: this.greeting(), params: { firstname: profile.firstname } };
    return { key: 'FREMDE_R', params: {} };
  });

  // Effect wird automatisch ausgeführt, wenn sich das Profile ändert
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private profileEffect = effect(async () => {
    const profile = this.profile();
    if (profile) {
      await this.findNextWorkingDayWithEntries(profile.id);
    }
  });

  private async findNextWorkingDayWithEntries(userId: number) {
    const today = dayjs();
    const maxDaysToCheck = 14; // Maximal 2 Wochen vorausschauen

    for (let i = 1; i <= maxDaysToCheck; i++) {
      const checkDate = today.add(i, 'day');

      // Sonntage überspringen — Samstage werden berücksichtigt, da SFG-Mitarbeiter samstags eingeplant sein können
      if (checkDate.day() === 0) {
        continue;
      }

      try {
        const montageplanData = await lastValueFrom(
          this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanBlocksByUserIdAndDaterange({
            userId: userId,
            fromDate: checkDate.format('YYYY-MM-DD'),
            toDate: checkDate.format('YYYY-MM-DD'),
          }),
        );

        // Prüfen ob an diesem Tag Einträge vorhanden sind
        if (montageplanData && montageplanData.length > 0) {
          const hasEntries = montageplanData.some(day =>
            day.teams && day.teams.length > 0 &&
            (day.teams.some(team => team.blocks && team.blocks.length > 0) ||
             day.teams.some(team => team.tasks && team.tasks.length > 0))
          );

          if (hasEntries) {
            this.toDate.set(checkDate.format('YYYY-MM-DD'));
            return;
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der Montageplandaten:', error);
      }
    }

    // Wenn kein Tag mit Einträgen gefunden wurde, nur heute anzeigen
    this.toDate.set(today.format('YYYY-MM-DD'));
  }

  protected async openProjectDetails(projectId: number) {
    console.log('openProjectDetails', projectId);
    await this.router.navigate(['mitarbeitende', 'projekte', 'details', projectId]);
  }

  protected async openUserDetails(userId: number) {
    await this.router.navigate(['mitarbeitende', 'adressbuch', 'details', userId]);
  }

  protected readonly dayjs = dayjs;
}
